import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Helmet } from "react-helmet";
import PageLayout from "../../components/navigation/PageLayout";
import Icon from "../../components/AppIcon";
import { useLocationContext } from "../../contexts/LocationContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { useEmployees } from "../../hooks/useEmployees";
import { useJobRoles } from "../../hooks/useJobRoles";
import { clockRecordApi } from "../../services/api";

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateTime = (d) => {
  if (!d) return "-";
  return `${formatDate(d)} ${formatTime(d)}`;
};

const formatHours = (h) => {
  if (h == null) return "-";
  return `${Number(h).toFixed(1)}h`;
};

const toISODate = (d) => d.toISOString().split("T")[0];

const getDateRange = (preset) => {
  const today = new Date();
  const to = toISODate(today);
  switch (preset) {
    case "7d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from: toISODate(from), to };
    }
    case "30d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 30);
      return { from: toISODate(from), to };
    }
    default:
      return null;
  }
};

// =============================================================================
// CSV Import Modal
// =============================================================================

const CsvImportModal = ({ isOpen, onClose, onImport, importing }) => {
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = () => {
    const templateHeaders = [
      "Employee Name",
      "Job Role",
      "Location",
      "Department",
      "Clock In",
      "Clock Out",
      "Break Minutes",
      "Hours Worked",
      "Notes",
    ];
    const exampleRow = [
      "John Smith",
      "Bartender",
      "Main Restaurant",
      "Bar",
      "2025-01-15 09:00",
      "2025-01-15 17:00",
      "30",
      "7.5",
      "Regular shift",
    ];
    const csv = [templateHeaders.join(","), exampleRow.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "clock-records-import-template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onImport(file);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1200]"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1201] w-full max-w-lg bg-white rounded-xl shadow-xl animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Import Clock Records</h3>
              <p className="text-sm text-muted-foreground">
                Upload a CSV file to import records
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Instructions */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium flex items-center gap-2">
              <Icon name="Info" size={16} className="text-primary" />
              Import Instructions
            </p>
            <ul className="text-sm text-muted-foreground space-y-1.5 ml-6">
              <li className="flex items-start gap-2">
                <span className="text-primary font-semibold">1.</span>
                Download the template CSV below
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-semibold">2.</span>
                Fill in employee records — <strong>Employee Name</strong>,{" "}
                <strong>Job Role</strong>, <strong>Location</strong>, and{" "}
                <strong>Clock In</strong> are required
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-semibold">3.</span>
                Employee names, job roles, and locations must match existing
                records exactly
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-semibold">4.</span>
                Date format should be{" "}
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                  YYYY-MM-DD HH:MM
                </code>
              </li>
            </ul>
          </div>

          {/* Download Template */}
          <button
            onClick={handleDownloadTemplate}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-primary/30 rounded-lg text-primary hover:bg-primary/5 hover:border-primary/50 transition-all"
          >
            <Icon name="FileDown" size={18} />
            <span className="font-medium text-sm">Download CSV Template</span>
          </button>

          {/* Upload Area */}
          <div>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="w-full flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-border rounded-lg hover:bg-muted/30 hover:border-muted-foreground/30 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <>
                  <div className="spinner" />
                  <span className="text-sm text-muted-foreground">
                    Importing records...
                  </span>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Icon
                      name="Upload"
                      size={24}
                      className="text-muted-foreground"
                    />
                  </div>
                  <span className="text-sm font-medium">
                    Click to select a CSV file
                  </span>
                  <span className="text-xs text-muted-foreground">
                    or drag and drop
                  </span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border bg-muted/30 rounded-b-xl flex justify-end">
          <button onClick={onClose} className="btn btn-outline btn-sm">
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

// =============================================================================
// Main Component
// =============================================================================

const ClockRecords = () => {
  const { currentLocation, hasLocations } = useLocationContext();
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();
  const fileInputRef = useRef(null);

  // Date range state
  const [datePreset, setDatePreset] = useState("30d");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toISODate(d);
  });
  const [dateTo, setDateTo] = useState(() => toISODate(new Date()));

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedJobRole, setSelectedJobRole] = useState("");
  const [sortField, setSortField] = useState("clockIn");
  const [sortDir, setSortDir] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);

  // Data
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const shouldFetchData = !isGlobalAdmin || selectedTenant;
  const { employees: apiEmployees, loading: employeesLoading } = useEmployees({
    autoFetch: shouldFetchData,
  });
  const { jobRoles, loading: rolesLoading } = useJobRoles({
    autoFetch: shouldFetchData,
    activeOnly: false,
  });

  // Handle date preset changes
  const handleDatePreset = useCallback((preset) => {
    setDatePreset(preset);
    if (preset === "custom") return;
    const range = getDateRange(preset);
    if (range) {
      setDateFrom(range.from);
      setDateTo(range.to);
    }
  }, []);

  // Handle custom date changes
  const handleCustomDateFrom = useCallback((val) => {
    setDatePreset("custom");
    setDateFrom(val);
  }, []);

  const handleCustomDateTo = useCallback((val) => {
    setDatePreset("custom");
    setDateTo(val);
  }, []);

  // Fetch clock records
  const fetchRecords = useCallback(async () => {
    if (!shouldFetchData) return;
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.fromDate = new Date(dateFrom).toISOString();
      if (dateTo) params.toDate = new Date(dateTo + "T23:59:59").toISOString();
      if (currentLocation?.id) params.locationId = currentLocation.id;
      const response = await clockRecordApi.getAll(params);
      if (response.success) {
        setRecords(response.data || []);
      } else {
        showNotification(
          response.message || "Failed to load clock records",
          "error",
        );
      }
    } catch (err) {
      showNotification("Failed to load clock records: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [
    shouldFetchData,
    selectedTenant?.id,
    dateFrom,
    dateTo,
    currentLocation?.id,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Employee map keyed by ID
  const employeeMap = useMemo(() => {
    const map = {};
    (apiEmployees || []).forEach((e) => {
      map[e.id] = e;
    });
    return map;
  }, [apiEmployees]);

  // Role map keyed by ID
  const roleMap = useMemo(() => {
    const map = {};
    (jobRoles || []).forEach((r) => {
      map[r.id] = r;
    });
    return map;
  }, [jobRoles]);

  // Departments from employees for filter
  const departments = useMemo(() => {
    const depts = new Set();
    (apiEmployees || []).forEach((e) => {
      if (e.department) depts.add(e.department);
    });
    return [...depts].sort();
  }, [apiEmployees]);

  // Apply client-side filters
  const filteredRecords = useMemo(() => {
    let result = records || [];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          (r.employeeName || "").toLowerCase().includes(lower) ||
          (r.location || "").toLowerCase().includes(lower),
      );
    }

    if (selectedEmployee) {
      result = result.filter((r) => r.employeeId === selectedEmployee);
    }

    if (selectedDepartment) {
      result = result.filter((r) => {
        const emp = employeeMap[r.employeeId];
        return emp?.department === selectedDepartment;
      });
    }

    if (selectedJobRole) {
      result = result.filter((r) => {
        const emp = employeeMap[r.employeeId];
        // Check both primaryJobRoleId and jobRoleId fields
        return (
          emp?.primaryJobRoleId === selectedJobRole ||
          emp?.jobRoleId === selectedJobRole
        );
      });
    }

    // Sort
    result = [...result].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === "clockIn" || sortField === "clockOut") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      if (sortField === "hoursWorked") {
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
      }
      if (sortField === "employeeName") {
        aVal = (aVal || "").toLowerCase();
        bVal = (bVal || "").toLowerCase();
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [
    records,
    searchTerm,
    selectedEmployee,
    selectedDepartment,
    selectedJobRole,
    sortField,
    sortDir,
    employeeMap,
  ]);

  // Summary stats
  const stats = useMemo(() => {
    const totalHours = filteredRecords.reduce(
      (sum, r) => sum + (r.hoursWorked || 0),
      0,
    );
    const uniqueEmployees = new Set(filteredRecords.map((r) => r.employeeId))
      .size;
    return {
      totalRecords: filteredRecords.length,
      totalHours: totalHours.toFixed(1),
      uniqueEmployees,
      avgHours:
        uniqueEmployees > 0 ? (totalHours / uniqueEmployees).toFixed(1) : "0.0",
    };
  }, [filteredRecords]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const handleExportCsv = () => {
    const headers = [
      "Employee",
      "Job Role",
      "Department",
      "Location",
      "Clock In",
      "Clock Out",
      "Hours Worked",
      "Status",
    ];
    const rows = filteredRecords.map((r) => {
      const emp = employeeMap[r.employeeId];
      const roleId = emp?.primaryJobRoleId || emp?.jobRoleId;
      const role = roleId ? roleMap[roleId] : null;
      return [
        r.employeeName || "Unknown",
        role?.name || emp?.jobTitle || "-",
        emp?.department || "-",
        r.location || "-",
        r.clockIn ? new Date(r.clockIn).toISOString() : "",
        r.clockOut ? new Date(r.clockOut).toISOString() : "",
        r.hoursWorked != null ? Number(r.hoursWorked).toFixed(2) : "",
        r.status || "-",
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clock-records-${currentLocation?.name || "all"}-${dateFrom}-${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showNotification(`Exported ${filteredRecords.length} records to CSV`);
  };

  const handleImportCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const response = await clockRecordApi.importCsv(file);
      if (response.success) {
        setImportResult(response.data);
        showNotification(
          `Imported ${response.data?.importedCount || 0} records`,
        );
        setShowImportModal(false);
        fetchRecords();
      } else {
        showNotification(response.message || "Import failed", "error");
      }
    } catch (err) {
      showNotification("Import failed: " + err.message, "error");
    } finally {
      setImporting(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedEmployee("");
    setSelectedDepartment("");
    setSelectedJobRole("");
  };

  const activeFilterCount = [
    searchTerm,
    selectedEmployee,
    selectedDepartment,
    selectedJobRole,
  ].filter(Boolean).length;

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Clock Records", path: "/clock-records" },
  ];

  // Global Admin tenant selection
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Clock Records - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Clock Records"
          description="View and manage employee clock records."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Please select a tenant from the sidebar to view clock records.
            </p>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Clock Records -{" "}
          {currentLocation?.name || selectedTenant?.name || "Rocket Tronc"}
        </title>
      </Helmet>

      <PageLayout
        title="Clock Records"
        description={
          currentLocation
            ? `${currentLocation.name} — View and manage employee clock-in/clock-out records.`
            : "View and manage employee clock-in/clock-out records across all locations."
        }
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCsv}
              className="btn btn-outline btn-sm"
              disabled={filteredRecords.length === 0}
            >
              <Icon name="Download" size={16} />
              Export
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn btn-primary btn-sm"
            >
              <Icon name="Upload" size={16} />
              Import CSV
            </button>
          </div>
        }
      >
        {/* Notification */}
        {notification && (
          <div
            className={`mb-4 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
            <Icon
              name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Import Result Banner */}
        {importResult && (
          <div className="mb-4 card p-4 border border-accent/20 bg-accent/5">
            <div className="flex items-start gap-3">
              <Icon name="FileUp" size={20} className="text-accent mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Import Complete</p>
                <p className="text-sm text-muted-foreground">
                  Total rows: {importResult.totalRows} Â· Imported:{" "}
                  {importResult.importedCount} Â· Skipped:{" "}
                  {importResult.skippedCount}
                </p>
                {importResult.errors?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-sm text-warning cursor-pointer">
                      {importResult.errors.length} warning(s)
                    </summary>
                    <ul className="mt-1 text-xs text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>
                          Row {err.row}: {err.message}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <button
                onClick={() => setImportResult(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Clock" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">
                  {stats.totalRecords}
                </p>
                <p className="text-xs text-muted-foreground">Records</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Icon name="Timer" size={20} className="text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">
                  {stats.totalHours}
                </p>
                <p className="text-xs text-muted-foreground">Total Hours</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="Users" size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">
                  {stats.uniqueEmployees}
                </p>
                <p className="text-xs text-muted-foreground">Employees</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Icon name="BarChart3" size={20} className="text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">
                  {stats.avgHours}h
                </p>
                <p className="text-xs text-muted-foreground">Avg / Employee</p>
              </div>
            </div>
          </div>
        </div>

        {/* Date Range & Filters Card */}
        <div className="card mb-6">
          {/* Date Range Row */}
          <div className="p-4 border-b border-border">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              {/* Date Presets */}
              <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                {[
                  { key: "7d", label: "Last 7 days" },
                  { key: "30d", label: "Last 30 days" },
                  { key: "custom", label: "Custom" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => handleDatePreset(opt.key)}
                    className={`px-3 py-1.5 border rounded-md text-sm font-medium transition-all ${
                      datePreset === opt.key
                        ? "bg-red-500 text-white shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Inputs - always visible for clarity */}
              <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 flex-1">
                <Icon
                  name="Calendar"
                  size={16}
                  className="text-muted-foreground shrink-0"
                />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => handleCustomDateFrom(e.target.value)}
                  className="input input-sm w-36"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => handleCustomDateTo(e.target.value)}
                  className="input input-sm w-36"
                />
              </div>

              {/* Refresh */}
              <div className="flex items-center gap-2 sm:ml-auto">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`btn btn-outline btn-sm ${activeFilterCount > 0 ? "border-primary text-primary" : ""}`}
                >
                  <Icon name="SlidersHorizontal" size={16} />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-1 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={fetchRecords}
                  className="btn btn-outline btn-sm"
                  disabled={loading}
                  title="Refresh records"
                >
                  <Icon
                    name="RefreshCw"
                    size={16}
                    className={loading ? "animate-spin" : ""}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="p-4 bg-muted/20 border-b border-border animate-slideIn">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Search */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Search
                  </label>
                  <div className="relative">
                    <Icon
                      name="Search"
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Employee or location..."
                      className="input input-sm pl-9 w-full"
                    />
                  </div>
                </div>

                {/* Employee Filter */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Employee
                  </label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="input input-sm w-full"
                  >
                    <option value="">All Employees</option>
                    {(apiEmployees || [])
                      .filter(
                        (e) =>
                          !currentLocation ||
                          e.locationId === currentLocation.id,
                      )
                      .sort((a, b) =>
                        (a.fullName || "").localeCompare(b.fullName || ""),
                      )
                      .map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.fullName || `${e.firstName} ${e.lastName}`}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Department Filter */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Department
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="input input-sm w-full"
                  >
                    <option value="">All Departments</option>
                    {departments.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Job Role Filter */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Job Role
                  </label>
                  <select
                    value={selectedJobRole}
                    onChange={(e) => setSelectedJobRole(e.target.value)}
                    className="input input-sm w-full"
                  >
                    <option value="">All Job Roles</option>
                    {(jobRoles || [])
                      .filter((r) => r.isActive)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Clear Filters */}
              {activeFilterCount > 0 && (
                <div className="mt-3 flex items-center justify-end">
                  <button
                    onClick={clearFilters}
                    className="btn btn-ghost btn-sm text-xs text-primary"
                  >
                    <Icon name="X" size={14} />
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="spinner mx-auto mb-4" />
              <p className="text-muted-foreground">Loading clock records...</p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon
                  name="Clock"
                  size={32}
                  className="text-muted-foreground"
                />
              </div>
              <h3 className="font-semibold mb-2">No clock records found</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {activeFilterCount > 0
                  ? "Try adjusting your filters or date range."
                  : "Import clock records via CSV or sync from your HR provider."}
              </p>
              {activeFilterCount === 0 && (
                <button
                  onClick={() => setShowImportModal(true)}
                  className="btn btn-primary btn-sm mt-4"
                >
                  <Icon name="Upload" size={16} />
                  Import Records
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {[
                      { key: "employeeName", label: "Employee" },
                      { key: "role", label: "Job Role" },
                      { key: "clockIn", label: "Clock In" },
                      { key: "clockOut", label: "Clock Out" },
                      { key: "hoursWorked", label: "Hours" },
                      { key: "location", label: "Location" },
                      { key: "status", label: "Status" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className="px-4 py-3 text-left font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none"
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {sortField === col.key && (
                            <Icon
                              name={
                                sortDir === "asc" ? "ChevronUp" : "ChevronDown"
                              }
                              size={14}
                            />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((r) => {
                    const emp = employeeMap[r.employeeId];
                    const roleId = emp?.primaryJobRoleId || emp?.jobRoleId;
                    const role = roleId ? roleMap[roleId] : null;
                    return (
                      <tr
                        key={r.id}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium">
                          {r.employeeName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {role?.name || emp?.jobTitle || "-"}
                        </td>
                        <td className="px-4 py-3">
                          {formatDateTime(r.clockIn)}
                        </td>
                        <td className="px-4 py-3">
                          {r.clockOut ? (
                            formatDateTime(r.clockOut)
                          ) : (
                            <span className="text-warning">In Progress</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium font-mono">
                          {formatHours(r.hoursWorked)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {r.location || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`badge text-xs ${
                              r.status === "Completed"
                                ? "badge-success"
                                : r.status === "InProgress"
                                  ? "badge-warning"
                                  : "badge-outline"
                            }`}
                          >
                            {r.status === "InProgress"
                              ? "In Progress"
                              : r.status || "-"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {filteredRecords.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-muted/30 text-sm text-muted-foreground flex items-center justify-between">
              <span>
                Showing {filteredRecords.length} of {records.length} records
              </span>
              {currentLocation && (
                <span className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} />
                  {currentLocation.name}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Import Modal */}
        <CsvImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportCsv}
          importing={importing}
        />
      </PageLayout>
    </>
  );
};

export default ClockRecords;
